import {
  DatePicker,
  Table,
  Tag,
  Button,
  Pagination,
  Select,
  message,
  Tooltip,
} from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import HeaderScreen from 'src/components/shared/header-screen/HeaderScreen'
import { CSVLink } from 'react-csv'
import { buildCsvString } from 'src/utils/csv'
import FadeIn from 'src/components/styled/FadeIn'
import styled from 'styled-components/macro'
import { API } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'
import { dateRangeCaption } from 'src/utils/dateRangeCaption'

const { Option } = Select

const AdminInvoiceExceptions = () => {
  const [exceptions, setExceptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  })
  const dateFormat = 'YYYY-MM-DD'
  const [filters, setFilters] = useState({
    status: undefined,
    resolved: undefined,
    dateFrom: undefined,
    dateTo: undefined,
  })

  useEffect(() => {
    fetchExceptions()
  }, [pagination.page, pagination.limit, filters])

  const fetchExceptions = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      })

      if (filters.status) {
        params.append('status', filters.status)
      }
      if (filters.resolved !== undefined) {
        params.append('resolved', filters.resolved.toString())
      }
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom)
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo)
      }

      const response = await API.get(
        'api2',
        `admin/invoices/exceptions?${params.toString()}`,
      )

      if (response.success) {
        setExceptions(response.exceptions || [])
        setPagination(prev => ({
          ...prev,
          total: response.total || 0,
        }))
      } else {
        message.error('Failed to fetch invoice exceptions')
      }
    } catch (error) {
      console.error('Error fetching invoice exceptions:', error)
      Sentry.captureException(error)
      message.error('Failed to fetch invoice exceptions')
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      page,
      limit: pageSize || prev.limit,
    }))
  }

  const handleDateRangeChange = (value, dateString) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: dateString[0] || undefined,
      dateTo: dateString[1] || undefined,
    }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleStatusFilter = value => {
    setFilters(prev => ({
      ...prev,
      status: value || undefined,
    }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleResolvedFilter = value => {
    setFilters(prev => ({
      ...prev,
      resolved: value !== undefined ? value === 'true' : undefined,
    }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  dayjs.extend(utc)

  const stringSort = (a, b) => String(a).trim().localeCompare(String(b).trim())

  const getStatusColor = status => {
    switch (status) {
      case 'blocked':
        return 'red'
      case 'exported_with_mismatches':
        return 'orange'
      case 'failed':
        return 'magenta'
      case 'need_retry':
        return 'blue'
      default:
        return 'default'
    }
  }

  const getStatusLabel = status => {
    switch (status) {
      case 'blocked':
        return 'Blocked'
      case 'exported_with_mismatches':
        return 'Exported with Mismatches'
      case 'failed':
        return 'Failed'
      case 'need_retry':
        return 'Need Retry'
      default:
        return status
    }
  }

  // CSV export
  const csvHeaders = [
    'Venue',
    'Supplier',
    'Invoice Number',
    'Invoice Date',
    'Status',
    'Error Message',
    'OMS Net',
    'OMS VAT',
    'OMS Gross',
    'Xero Net',
    'Xero VAT',
    'Xero Gross',
    'Attempt Count',
    'Last Attempt',
    'Created At',
    'Resolved',
  ]

  const csvRows = exceptions.map(exception => [
    exception.venue?.title || '',
    exception.supplier?.title || '',
    exception.invoiceNumber || '',
    exception.invoiceDate
      ? dayjs(exception.invoiceDate).format('DD/MM/YYYY')
      : '',
    getStatusLabel(exception.status),
    exception.errorMessage || '',
    exception.xeroExport?.omsValues?.netTotal
      ? `£${exception.xeroExport.omsValues.netTotal.toFixed(2)}`
      : '',
    exception.xeroExport?.omsValues?.vatTotal
      ? `£${exception.xeroExport.omsValues.vatTotal.toFixed(2)}`
      : '',
    exception.xeroExport?.omsValues?.grossTotal
      ? `£${exception.xeroExport.omsValues.grossTotal.toFixed(2)}`
      : '',
    exception.xeroExport?.xeroValues?.netTotal
      ? `£${exception.xeroExport.xeroValues.netTotal.toFixed(2)}`
      : '',
    exception.xeroExport?.xeroValues?.vatTotal
      ? `£${exception.xeroExport.xeroValues.vatTotal.toFixed(2)}`
      : '',
    exception.xeroExport?.xeroValues?.grossTotal
      ? `£${exception.xeroExport.xeroValues.grossTotal.toFixed(2)}`
      : '',
    exception.xeroExport?.attemptCount || 0,
    exception.xeroExport?.lastAttempt
      ? dayjs(exception.xeroExport.lastAttempt).format('DD/MM/YYYY HH:mm')
      : '',
    exception.createdAt
      ? dayjs(exception.createdAt).format('DD/MM/YYYY HH:mm')
      : '',
    exception.resolved ? 'Yes' : 'No',
  ])

  return (
    <Wrapper>
      <HeaderScreen title={'Invoice Exceptions'}>
        <Button type={'primary'} style={{ width: 160, height: 40 }}>
          <CSVLink
            data={buildCsvString([csvHeaders, ...csvRows])}
            filename={`Invoice_Exceptions_${moment().format('YYYY-MM-DD')}.csv`}
          >
            Download CSV
          </CSVLink>
        </Button>
      </HeaderScreen>
      <SearchFilterBlock>
        <DateControl>
          <DateCaption>
            {dateRangeCaption(filters.dateFrom, filters.dateTo)}
          </DateCaption>
          <StyledRangePicker
            format={dateFormat}
            value={
              filters.dateFrom && filters.dateTo
                ? [moment(filters.dateFrom), moment(filters.dateTo)]
                : []
            }
            onChange={handleDateRangeChange}
          />
        </DateControl>
        <Select
          placeholder='Filter by Status'
          allowClear
          style={{ width: 200 }}
          onChange={handleStatusFilter}
          value={filters.status}
        >
          <Option value='blocked'>Blocked</Option>
          <Option value='exported_with_mismatches'>
            Exported with Mismatches
          </Option>
          <Option value='failed'>Failed</Option>
          <Option value='need_retry'>Need Retry</Option>
        </Select>
        <Select
          placeholder='Filter by Resolution'
          allowClear
          style={{ width: 200 }}
          onChange={handleResolvedFilter}
          value={
            filters.resolved !== undefined
              ? filters.resolved.toString()
              : undefined
          }
        >
          <Option value='false'>Unresolved</Option>
          <Option value='true'>Resolved</Option>
        </Select>
      </SearchFilterBlock>
      <div style={{ margin: '8px 0' }}>
        {`Total: ${pagination.total} exceptions`}
      </div>
      <Table
        scroll={{
          scrollToFirstRowOnChange: true,
          x: true,
        }}
        dataSource={exceptions.map(data => ({
          ...data,
          key: data.id,
        }))}
        loading={loading}
        pagination={false}
        columns={[
          {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            className: 'restrictShrink',
            render: venue => venue?.title || '-',
            sorter: (a, b) =>
              stringSort(a.venue?.title || '', b.venue?.title || ''),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Supplier',
            dataIndex: 'supplier',
            key: 'supplier',
            className: 'restrictShrink',
            render: supplier => supplier?.title || '-',
            sorter: (a, b) =>
              stringSort(a.supplier?.title || '', b.supplier?.title || ''),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Invoice Number',
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            className: 'restrictShrink',
            sorter: (a, b) =>
              stringSort(a.invoiceNumber || '', b.invoiceNumber || ''),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Invoice Date',
            dataIndex: 'invoiceDate',
            key: 'invoiceDate',
            className: 'restrictShrink',
            render: date => (date ? dayjs(date).format('DD/MM/YYYY') : '-'),
            sorter: (a, b) => {
              if (!a.invoiceDate && !b.invoiceDate) return 0
              if (!a.invoiceDate) return 1
              if (!b.invoiceDate) return -1
              return (
                dayjs(a.invoiceDate).valueOf() - dayjs(b.invoiceDate).valueOf()
              )
            },
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            className: 'restrictShrink',
            render: status => (
              <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
            ),
            sorter: (a, b) => stringSort(a.status, b.status),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Error Message',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            className: 'restrictShrink',
            ellipsis: {
              showTitle: false,
            },
            render: (errorMessage, row) => (
              <Tooltip title={row.errorDetail || errorMessage || undefined}>
                <div>{errorMessage || '-'}</div>
              </Tooltip>
            ),
          },
          {
            title: 'Attempts',
            dataIndex: ['xeroExport', 'attemptCount'],
            key: 'attemptCount',
            className: 'restrictShrink',
            render: count => count || 0,
            sorter: (a, b) =>
              (a.xeroExport?.attemptCount || 0) -
              (b.xeroExport?.attemptCount || 0),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Last Attempt',
            dataIndex: ['xeroExport', 'lastAttempt'],
            key: 'lastAttempt',
            className: 'restrictShrink',
            render: date =>
              date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
            sorter: (a, b) => {
              const dateA = a.xeroExport?.lastAttempt
              const dateB = b.xeroExport?.lastAttempt
              if (!dateA && !dateB) return 0
              if (!dateA) return 1
              if (!dateB) return -1
              return dayjs(dateA).valueOf() - dayjs(dateB).valueOf()
            },
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            className: 'restrictShrink',
            render: date => dayjs(date).format('DD/MM/YYYY HH:mm'),
            sorter: (a, b) =>
              dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Resolved',
            dataIndex: 'resolved',
            key: 'resolved',
            className: 'restrictShrink',
            render: resolved => (
              <Tag color={resolved ? 'green' : 'default'}>
                {resolved ? 'Yes' : 'No'}
              </Tag>
            ),
            filters: [
              { text: 'Resolved', value: true },
              { text: 'Unresolved', value: false },
            ],
            onFilter: (value, record) => record.resolved === value,
          },
        ]}
      />
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <Pagination
          current={pagination.page}
          pageSize={pagination.limit}
          total={pagination.total}
          onChange={handlePageChange}
          onShowSizeChange={handlePageChange}
          showSizeChanger={pagination.total > 50}
          pageSizeOptions={['25', '50', '100', '200']}
          showTotal={(total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} exceptions`
          }
          hideOnSinglePage
        />
      </div>
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  .restrictShrink {
    min-width: 95px;
  }
`

const DateControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DateCaption = styled.span`
  font-weight: 600;
  white-space: nowrap;
`

const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
    width: 280px;
  }
  .ant-calendar-range-picker-input {
    width: 42%;
  }
`

const SearchFilterBlock = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`

export default AdminInvoiceExceptions
